import {ReactElement, useEffect} from "react";
import {
    DataGridPremium,
    GridColDef,
    GridRenderCellParams,
    GridValueGetter,
    GridValidRowModel
} from "@mui/x-data-grid-premium";
import {DividendFilterConfig} from "../domain/assetmanager/DividendFilterConfig";
import {
    useCreateDividendFilterDefaultConfiguration,
    useGetDividendFilterConfigurations
} from "../application/DividendConfigService";
import {GetDataApi} from "./types/GetDataApi";
import {Link, useNavigate} from "react-router-dom";
import Grid from "@mui/material/Grid";
import CreateDividendAnyConfigurationDialog from "./CreateDividendAnyConfigurationDialog";
import {CashDividendSource, toHumanReadableFormat} from "../domain/dividend/CashDividendSource";
import {ResponseDataFunction} from "./types/ResponseDataFunction";
import Title from "./Title";
import {useAutoSizeGrid} from "./AutoSizeGridHook";

// Helper type to extract the row type from GridValueGetter for proper typing
type ValueGetterParams<R extends GridValidRowModel> = Parameters<GridValueGetter<R>>;
type ValueGetterRow<R extends GridValidRowModel> = ValueGetterParams<R>[1]; // row is the second parameter

export default function DividendFilterConfigurationsComponent(): ReactElement {
    const navigation = useNavigate();
    const getFunction: GetDataApi<DividendFilterConfig[]> = useGetDividendFilterConfigurations();
    const createFunction: ResponseDataFunction<CashDividendSource, DividendFilterConfig> = useCreateDividendFilterDefaultConfiguration();
    const apiRef = useAutoSizeGrid<DividendFilterConfig>({
        data: getFunction.data,
        loading: getFunction.loading,
        columns: [
            'id',
            'source',
            'instrumentPrefix',
            'accountNumber'
        ]
    });
    const initialState =
        {
            columns: {
                dimensions: {
                    id: { width: 100},
                    source: {width: 150},
                    instrumentPrefix: {width: 300},
                    accountNumber: {width: 300},
                }
            }
        }

    useEffect(() => {
        if (createFunction.error) {
            void navigation("dividends/config");
        } else if (createFunction.data.id > 0) {
            void navigation("filter/" + createFunction.data.id);
        }
    }, [ createFunction.data, createFunction.error ]);

    const columns: GridColDef<DividendFilterConfig>[] = [
        {
            field: 'id', headerName: 'ID',
            renderCell: (params: GridRenderCellParams<DividendFilterConfig>) => <Link to={ "filter/" + params.row.id.toString() }>{params.row.id}</Link>
        },
        { field: 'source', headerName: 'Dividend Source', valueGetter: (_value, row: ValueGetterRow<DividendFilterConfig>) => { return toHumanReadableFormat(row.source)}},
        { field: 'accountNumber', headerName: 'Account Number'},
        { field: 'instrumentPrefix', headerName: "Instrument Prefix"}
    ];

    return (
        <>
            <Grid
                container
                spacing={2}
                sx={{
                    alignItems: "center",
                    justifyContent: "space-between"
                }}>
                <Grid size={{ xs: 12, md: 8, lg: 8 }}>
                    <Title>Filter Configuration</Title>
                </Grid>
                <Grid size={{ xs: 12, md: 3, lg: 3 }}>
                    <CreateDividendAnyConfigurationDialog
                        title={ "Filter"}
                        createFunction={ createFunction }
                        initialSource={ CashDividendSource.VPB }
                        urlPath={ "filter" }
                        toolTip={ "Rules for filtering out dividends that we do not want imported." }
                        usage={ "This creates filters that omits data read from the dividend reports when filter matches." }
                    />
                </Grid>
            </Grid>
            <Grid container spacing={2} sx={{ mt: 2 }}>
                <Grid size={{ xs: 12, md: 12, lg: 12 }}>
                    <DataGridPremium
                        apiRef={apiRef}
                        initialState={initialState}
                        loading={getFunction.loading}
                        rows={getFunction.data}
                        columns={columns}
                        density={"compact"}
                        getRowId={(row) => row.id}
                        autoHeight={true}
                        hideFooter={true}
                    />
                </Grid>
            </Grid>
        </>
    );

}